"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Chat/Charts/EdgenChart";

import { useMemo } from "react";
import {
  ActionProvider,
  Renderer,
  StateProvider,
  VisibilityProvider,
  type Spec,
} from "@json-render/react";
import type { ChartData, ChartRenderContext } from "./edgen-charts/types";
import {
  edgenChartRegistry,
  validateEdgenChartData,
} from "./edgen-charts/catalog";
import { isEdgenChartComponentType } from "./edgen-charts/chartComponentTypes";
import {
  ChatJsonRenderContextProvider,
  type ChatJsonRenderContextValue,
} from "../jsonRender/ChatJsonRenderContext";
import { JsonRenderRuntimeContextProvider } from "../jsonRender/JsonRenderRuntimeContext";

const I18n = I18nUtil.createScoped(Translations);

const EMBED_SCALE = 0.5;
const TARGET_FPS = 30;
const STATIC_FRAME = 300;

interface EdgenChartProps {
  data?: string;
  spec?: Spec;
  renderContext?: ChatJsonRenderContextValue;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const extractRootChartData = (spec: Spec): unknown => {
  const elements = spec.elements as Record<string, unknown>;
  const rootElement = elements[spec.root];
  if (!isRecord(rootElement)) {
    return null;
  }
  const props = isRecord(rootElement.props) ? rootElement.props : {};
  return { ...props, type: rootElement.type };
};

function buildSpecFromChartData(chartData: ChartData): Spec {
  return {
    root: "chart",
    elements: {
      chart: {
        type: chartData.type,
        props: { ...chartData },
      },
    },
  };
}

function withRuntimeContext(inputSpec: Spec, ctx: ChartRenderContext): Spec {
  const elements = Object.fromEntries(
    Object.entries(inputSpec.elements).map(([key, element]) => {
      if (!isRecord(element) || !isEdgenChartComponentType(element.type)) {
        return [key, element];
      }

      const props = isRecord(element.props) ? element.props : {};

      return [
        key,
        {
          ...element,
          props: {
            ...props,
            ctx,
          },
        },
      ];
    }),
  );

  return {
    ...inputSpec,
    elements,
  };
}

const EdgenChart = ({
  data: dataStr,
  spec: inputSpec,
  renderContext,
}: EdgenChartProps) => {
  const parsedLegacySpec = useMemo<Spec | null>(() => {
    if (!dataStr) {
      return null;
    }
    try {
      const parsed = JSON.parse(dataStr);
      return buildSpecFromChartData(parsed);
    } catch {
      return null;
    }
  }, [dataStr]);

  const spec = useMemo(() => {
    const baseSpec = inputSpec ?? parsedLegacySpec;
    if (!baseSpec) {
      return null;
    }

    const ctx: ChartRenderContext = {
      frame: STATIC_FRAME,
      fps: TARGET_FPS,
      slideStart: 0,
      S: EMBED_SCALE,
      embed: true,
    };

    return withRuntimeContext(baseSpec, ctx);
  }, [inputSpec, parsedLegacySpec]);

  if (!spec) {
    return null;
  }

  const chartData = extractRootChartData(spec);
  if (!validateEdgenChartData(chartData)) {
    throw new Error("Invalid edgen chart data");
  }

  return (
    <div className="my-4" style={{ isolation: "isolate" }}>
      <ChatJsonRenderContextProvider value={renderContext}>
        <JsonRenderRuntimeContextProvider
          value={{
            scene: "chat",
            chatId: renderContext?.chatId,
            streamLoading: renderContext?.streamLoading,
            collapsed: renderContext?.collapsed,
            metrics: renderContext?.metrics,
            notificationId: renderContext?.notificationId,
          }}
        >
          <StateProvider initialState={{}}>
            <VisibilityProvider>
              <ActionProvider handlers={{}}>
                <Renderer
                  spec={spec}
                  registry={edgenChartRegistry}
                  fallback={() => (
                    <div className="text-content_tertiary body_s py-2">
                      {I18n.t("Unsupported chart type")}
                    </div>
                  )}
                />
              </ActionProvider>
            </VisibilityProvider>
          </StateProvider>
        </JsonRenderRuntimeContextProvider>
      </ChatJsonRenderContextProvider>
    </div>
  );
};

export default EdgenChart;
